import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Segmented,
  notification,
  Spin,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { contracts, clientsApi } from '../api';
import { userRole, employeeId } from '../../GetRole.utils';
import StepDrawer from '../other/StepDrawer';
import {
  Field,
  EntityChoice,
  ClientSelect,
  ProjectSelect,
  TermFields,
  FileDrop,
  AmountInput,
  Summary,
  entityName,
  apiErrorMessage,
  parseApiDate,
  formatDate,
  splitAmount,
  formatAmount,
} from '../other/contractFields';
import './sowDrawer.css';

const STATUS = { draft: 1, pending: 2 };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// optional country code, 10-15 digits, not all the same digit
const MOBILE_REGEX = /^(?:\+\d{1,3}[\s-]?)?(?!(\d)\1{9,14}$)\d{10,15}$/;

const PAYMENT_TERMS = [30, 45, 60, 90];
const LEAVE_TERMS = ['Monthly', 'Quarterly', 'Yearly'];
// values must match the server's validator ('HalfYearly' has no space)
const INVOICE_TENURES = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'HalfYearly', label: 'Half yearly' },
  { value: 'Yearly', label: 'Yearly' },
];

// the server validator rejects null / empty strings for typed fields, so leave them out
const withoutEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ''));

const cleanName = (value) =>
  value.replace(/[^\w\s]/gi, '').replace(/\b\w/g, (c) => c.toUpperCase());

const cleanMobile = (value) => value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');

// returns an error message, or '' when the number is fine
const mobileError = (value) => {
  const v = String(value || '');
  if (!v) return 'Enter the mobile number';
  if (v.replace(/\D/g, '').length < 10) return 'Mobile number must have at least 10 digits';
  if (!MOBILE_REGEX.test(v)) return 'Enter a valid mobile number, e.g. +919876543210';
  return '';
};

const emptyValues = (prefill) => ({
  seId: prefill?.seId ?? 1,
  clientId: prefill?.clientId ?? null,
  clientLabel: prefill?.legalEntityName ?? '',
  projectId: prefill?.projectId ?? null,
  projectLabel: prefill?.projectName ?? '',
  sowNumber: '',
  start: null,
  end: null,
  signatoryName: '',
  signatoryEmail: '',
  signatoryMobile: '',
  spanideaSignatory: null,
  pocChoice: null, // index into the client's contact list, or 'new'
  newPoc: { name: '', email: '', number: '' },
  paymentTerm: null, // 30 | 45 | 60 | 90 | 'other'
  customTerm: null,
  leavesAllowed: '',
  leavesTerm: 'Monthly',
  paymentType: 'T&M',
  invoiceTenure: 'Monthly',
  invoiceDate: dayjs(),
  currency: '₹',
  amount: '',
  milestones: [],
});

// a contact card is only shown when it has a name, an email and a mobile number
const isCompletePoc = (poc) =>
  [poc?.pocName, poc?.pocEmail, poc?.pocNumber].every((v) => v != null && String(v).trim() !== '');

const milestoneTotal = (list) =>
  Math.round(list.reduce((acc, m) => acc + (Number(m.percent) || 0), 0) * 100) / 100;

// Create or edit a SOW in a step drawer.
// contractId + status: edit. prefill: lock client/project (from the project page).
const SowDrawer = ({ open, onClose, contractId, status, prefill }) => {
  const isEdit = !!contractId;
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(emptyValues(prefill));
  const [file, setFile] = useState(null);
  const [existingFileName, setExistingFileName] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [pocList, setPocList] = useState([]);

  const canSendForApproval = userRole !== 'finance' && (!isEdit || status === 'draft');
  // editing a draft: only the contracting entity is disabled, every other field stays enabled
  const isDraftEdit = isEdit && status === 'draft';
  const entityLocked = isEdit || !!prefill?.clientId;
  // opened from a project page (prefill): client and project stay fixed, also when continuing a draft.
  // Elsewhere, editing a draft keeps them enabled.
  const clientLocked = isDraftEdit && !prefill?.clientId ? false : isEdit || !!prefill?.clientId;
  const projectLocked = isDraftEdit && !prefill?.projectId ? false : isEdit || !!prefill?.projectId;

  useEffect(() => {
    clientsApi
      .getEmployeeName()
      .then((res) => setEmployees((res.data || []).map((e) => ({ value: e.empCode, label: e.name }))))
      .catch((err) => console.error('Error fetching employees:', err));
  }, []);

  // reset / load when the drawer opens
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setFile(null);
    setExistingFileName('');
    setValues(emptyValues(prefill));
    if (!isEdit) return;

    setLoading(true);
    contracts
      .getContractsData(contractId)
      .then((res) => {
        const data = res.data[0]?.contractData || {};
        const { currency, amount } = splitAmount(data.totalAmount);
        const term = data.paymentTerm == null ? null : Number(data.paymentTerm);
        setValues({
          ...emptyValues(),
          seId: Number(data.seId),
          clientId: data.client?.clientId ?? null,
          clientLabel: data.client?.legalEntityName ?? '',
          projectId: data.project?.projectId ?? null,
          projectLabel: data.project?.projectName ?? '',
          sowNumber: data.sowNumber || '',
          start: parseApiDate(data.activationDate),
          end: parseApiDate(data.expiryDate),
          signatoryName: data.clientAuthorizer?.name || '',
          signatoryEmail: data.clientAuthorizer?.email || '',
          signatoryMobile: data.clientAuthorizer?.number ? String(data.clientAuthorizer.number) : '',
          spanideaSignatory: data.spanideaAuthorizerEmpName?.empCode ?? null,
          pocChoice: data.pocDetail?.name ? 'new' : null,
          newPoc: {
            name: data.pocDetail?.name || '',
            email: data.pocDetail?.email || '',
            number: data.pocDetail?.number ? String(data.pocDetail.number) : '',
          },
          paymentTerm: term == null ? null : PAYMENT_TERMS.includes(term) ? term : 'other',
          customTerm: term != null && !PAYMENT_TERMS.includes(term) ? term : null,
          leavesAllowed: data.leavesAllowed != null ? String(data.leavesAllowed) : '',
          leavesTerm: data.leavesTerm || 'Monthly',
          paymentType: data.paymentType || 'T&M',
          invoiceTenure: (data.invoiceTenure || 'Monthly').replace(' ', ''),
          invoiceDate: parseApiDate(data.tentativeInvoiceDate) || dayjs(),
          currency,
          amount,
          milestones: (data.milestone || []).map((m) => ({
            name: m.milestoneName || '',
            percent: m.milestonePercentage != null ? Number(m.milestonePercentage) : null,
            date: parseApiDate(m.milestoneDate),
          })),
        });
        setExistingFileName(data.fileName || '');
      })
      .catch((err) => console.error('Error fetching SOW:', err))
      .finally(() => setLoading(false));
    // prefill is read once when the drawer opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  // the client's contacts, plus the client's usual invoice day
  useEffect(() => {
    if (!open || !values.clientId) {
      setPocList([]);
      return;
    }
    contracts
      .getpocDropDown(values.clientId)
      .then((res) => {
        const list = (res?.data?.pocList || []).filter(isCompletePoc);
        setPocList(list);
        setValues((prev) => {
          const next = { ...prev };
          // an edited SOW's contact: select it in the list when it is one of the client's contacts
          if (prev.pocChoice === 'new' && prev.newPoc.email) {
            const index = list.findIndex((p) => p.pocEmail === prev.newPoc.email);
            if (index >= 0) next.pocChoice = index;
          }
          if (!isEdit && res?.data?.suggestedInvoiceDate) {
            next.invoiceDate = parseApiDate(res.data.suggestedInvoiceDate) || prev.invoiceDate;
          }
          return next;
        });
      })
      .catch((err) => console.error('Error fetching contacts:', err));
  }, [open, values.clientId, isEdit]);

  const update = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      if (patch.end !== undefined) delete next.end;
      if (patch.newPoc) {
        delete next.poc;
        delete next.pocNumber;
      }
      if (patch.milestones) delete next.milestones;
      return next;
    });
  };

  const setMilestone = (index, patch) =>
    update({
      milestones: values.milestones.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    });

  const stepErrors = (index) => {
    const e = {};
    if (index === 0) {
      if (!values.clientId) e.clientId = 'Choose a client';
      if (!values.projectId) e.projectId = 'Choose a project';
      if (!values.sowNumber) e.sowNumber = 'Enter the SOW number';
      if (!values.start) e.start = 'Choose a start date';
      if (!values.end) e.end = 'Choose an end date';
    }
    if (index === 1) {
      if (!values.signatoryName.trim()) e.signatoryName = 'Enter the signatory name';
      if (!EMAIL_REGEX.test(values.signatoryEmail)) e.signatoryEmail = 'Enter a valid email address';
      const signatoryMobileMsg = mobileError(values.signatoryMobile);
      if (signatoryMobileMsg) e.signatoryMobile = signatoryMobileMsg;
      if (!values.spanideaSignatory) e.spanideaSignatory = 'Choose the Spanidea signatory';
      if (values.pocChoice === null) e.poc = 'Choose a point of contact, or add one';
      if (values.pocChoice === 'new') {
        const { name, email, number } = values.newPoc;
        if (!name.trim() || !EMAIL_REGEX.test(email)) {
          e.poc = 'Enter the contact name and a valid email';
        }
        const pocMobileMsg = mobileError(number);
        if (pocMobileMsg) e.pocNumber = pocMobileMsg;
      }
    }
    if (index === 2) {
      if (!values.paymentTerm) e.paymentTerm = 'Choose a payment term';
      if (values.paymentTerm === 'other' && !(values.customTerm >= 1 && values.customTerm <= 365)) {
        e.paymentTerm = 'Enter the number of days (1–365)';
      }
      if (!/^[1-9][0-9]*$/.test(values.leavesAllowed)) e.leavesAllowed = 'Enter a whole number of days';
      if (values.paymentType === 'T&M') {
        if (!values.invoiceTenure) e.invoiceTenure = 'Choose how often to invoice';
        if (!values.invoiceDate) e.invoiceDate = 'Choose the invoice day';
      } else {
        if (!values.amount) e.amount = 'Enter the SOW amount';
        const incomplete = values.milestones.some(
          (m) => !m.name.trim() || !(m.percent > 0) || !m.date
        );
        const total = milestoneTotal(values.milestones);
        if (values.milestones.length === 0) e.milestones = 'Add at least one milestone';
        else if (incomplete) e.milestones = 'Every milestone needs a name, a percentage and a date';
        else if (total !== 100) e.milestones = `Milestones add up to ${total}%. They must add up to 100%.`;
      }
    }
    if (index === 3 && !file && !existingFileName) e.file = 'Upload the signed SOW';
    return e;
  };

  const goNext = () => {
    const e = stepErrors(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const selectedPoc =
    values.pocChoice === 'new'
      ? { pocName: values.newPoc.name, pocEmail: values.newPoc.email, pocNumber: values.newPoc.number }
      : values.pocChoice !== null
      ? pocList[values.pocChoice]
      : null;

  const save = (cStatusId) => {
    if (cStatusId === STATUS.pending) {
      for (let i = 0; i < 4; i++) {
        const e = stepErrors(i);
        if (Object.keys(e).length) {
          setErrors(e);
          setStep(i);
          return;
        }
      }
    }

    // a draft may be incomplete, but a mobile number that is entered must be valid
    if (cStatusId === STATUS.draft) {
      const draftErrors = {};
      if (values.signatoryMobile && mobileError(values.signatoryMobile)) {
        draftErrors.signatoryMobile = mobileError(values.signatoryMobile);
      }
      if (values.pocChoice === 'new' && values.newPoc.number && mobileError(values.newPoc.number)) {
        draftErrors.pocNumber = mobileError(values.newPoc.number);
      }
      if (Object.keys(draftErrors).length) {
        setErrors(draftErrors);
        setStep(1);
        return;
      }
    }

    const obj = {
      seId: Number(values.seId),
      clientId: values.clientId,
      contractTypeId: 2,
      cStatusId,
      projectId: values.projectId,
      sowNumber: String(values.sowNumber),
      activationDate: values.start,
      expiryDate: values.end,
      clientAuthorizerName: values.signatoryName,
      clientAuthorizerEmail: values.signatoryEmail,
      clientAuthorizerMobile: String(values.signatoryMobile),
      spanideaAuthorizerEmpCode: values.spanideaSignatory,
      pocName: selectedPoc?.pocName,
      pocEmail: selectedPoc?.pocEmail,
      pocNumber: selectedPoc?.pocNumber != null ? String(selectedPoc.pocNumber) : undefined,
      paymentTerm: values.paymentTerm === 'other' ? values.customTerm : values.paymentTerm,
      leavesAllowed: values.leavesAllowed,
      leavesTerm: values.leavesTerm,
      tentativeInvoiceDate: values.invoiceDate,
      paymentType: String(values.paymentType),
      createdBy: employeeId,
    };
    if (values.paymentType === 'T&M') {
      obj.invoiceTenure = values.invoiceTenure;
    } else {
      obj.totalAmount = values.currency + values.amount;
      obj.milestone = values.milestones.map((m) =>
        withoutEmpty({
          milestoneName: m.name.trim(),
          milestonePercentage: m.percent,
          milestoneDate: m.date,
        })
      );
      if (!values.amount) delete obj.totalAmount;
    }

    const formdata = new FormData();
    if (file) formdata.append('file', file);
    formdata.append('obj', JSON.stringify(withoutEmpty(obj)));

    setSaving(cStatusId === STATUS.pending ? 'approval' : 'draft');
    const request = isEdit
      ? contracts.updateContractsData(contractId, formdata)
      : contracts.addContractsData(formdata);

    request
      .then((res) => {
        notification.success({
          message: cStatusId === STATUS.pending ? 'SOW sent for approval' : res?.message || 'SOW saved',
        });
        onClose(true);
      })
      .catch((err) => {
        notification.error({
          message: apiErrorMessage(err, 'Could not save the SOW'),
        });
      })
      .finally(() => setSaving(null));
  };

  const employeeLabel = employees.find((e) => e.value === values.spanideaSignatory)?.label;
  const total = milestoneTotal(values.milestones);
  const termDays = values.paymentTerm === 'other' ? values.customTerm : values.paymentTerm;

  const steps = [
    {
      key: 'basics',
      title: 'Basics',
      content: loading ? (
        <Spin />
      ) : (
        <div className="sd-form">
          <EntityChoice
            value={values.seId}
            onChange={(seId) =>
              update({ seId, clientId: null, clientLabel: '', projectId: null, projectLabel: '', pocChoice: null })
            }
            disabled={entityLocked}
          />
          <ClientSelect
            seId={values.seId}
            value={values.clientId}
            onChange={(clientId, option) =>
              update({ clientId, clientLabel: option?.label || '', projectId: null, projectLabel: '', pocChoice: null })
            }
            disabled={clientLocked}
            currentOption={values.clientId ? { value: values.clientId, label: values.clientLabel } : null}
            error={errors.clientId}
          />
          <ProjectSelect
            clientId={values.clientId}
            value={values.projectId}
            onChange={(projectId, option) => update({ projectId, projectLabel: option?.label || '' })}
            disabled={projectLocked}
            currentOption={values.projectId ? { value: values.projectId, label: values.projectLabel } : null}
            error={errors.projectId}
          />
          <Field label="SOW number" required error={errors.sowNumber} help="Letters, numbers, - # / (max 30)">
            <Input
              size="large"
              maxLength={30}
              value={values.sowNumber}
              status={errors.sowNumber ? 'error' : ''}
              placeholder="e.g. SOW-2026-014"
              onChange={(e) =>
                update({ sowNumber: e.target.value.replace(/[^a-zA-Z0-9-#/]/g, '').toUpperCase() })
              }
            />
          </Field>
          <TermFields
            start={values.start}
            end={values.end}
            endless={false}
            allowEndless={false}
            onChange={update}
            errors={errors}
          />
        </div>
      ),
    },
    {
      key: 'people',
      title: 'People',
      content: (
        <div className="sd-form">
          <div className="sd-fieldset">
            <span className="sd-fieldset-title">Client signatory</span>
            <Field label="Name" required error={errors.signatoryName}>
              <Input
                size="large"
                value={values.signatoryName}
                status={errors.signatoryName ? 'error' : ''}
                placeholder="Full name"
                onChange={(e) => update({ signatoryName: cleanName(e.target.value) })}
              />
            </Field>
            <div className="sd-row">
              <Field label="Email" required error={errors.signatoryEmail}>
                <Input
                  size="large"
                  type="email"
                  value={values.signatoryEmail}
                  status={errors.signatoryEmail ? 'error' : ''}
                  placeholder="name@company.com"
                  onChange={(e) => update({ signatoryEmail: e.target.value.trim() })}
                />
              </Field>
              <Field label="Mobile" required error={errors.signatoryMobile}>
                <Input
                  size="large"
                  inputMode="tel"
                  maxLength={15}
                  value={values.signatoryMobile}
                  status={errors.signatoryMobile ? 'error' : ''}
                  onBlur={() =>
                    values.signatoryMobile &&
                    setErrors((prev) => ({ ...prev, signatoryMobile: mobileError(values.signatoryMobile) || undefined }))
                  }
                  placeholder="+919876543210"
                  onChange={(e) => update({ signatoryMobile: cleanMobile(e.target.value) })}
                />
              </Field>
            </div>
          </div>

          <Field label="Spanidea signatory" required error={errors.spanideaSignatory}>
            <Select
              showSearch
              size="large"
              placeholder="Choose a person"
              value={values.spanideaSignatory}
              onChange={(spanideaSignatory) => update({ spanideaSignatory })}
              optionFilterProp="label"
              options={employees}
              status={errors.spanideaSignatory ? 'error' : ''}
            />
          </Field>

          <Field label="Client point of contact" required error={errors.poc}>
            <div className="sow-poc-list" role="radiogroup" aria-label="Client point of contact">
              {pocList.map((poc, index) => (
                <button
                  key={`${poc.pocEmail}-${index}`}
                  type="button"
                  role="radio"
                  aria-checked={values.pocChoice === index}
                  className={`sd-choice ${values.pocChoice === index ? 'is-on' : ''}`}
                  onClick={() => update({ pocChoice: index })}
                >
                  <span className="sd-choice-title">{poc.pocName}</span>
                  <span className="sd-choice-sub">
                    {[poc.pocEmail, poc.pocNumber].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
              <button
                type="button"
                role="radio"
                aria-checked={values.pocChoice === 'new'}
                className={`sd-choice sow-poc-new ${values.pocChoice === 'new' ? 'is-on' : ''}`}
                onClick={() => update({ pocChoice: 'new' })}
              >
                <span className="sd-choice-title">
                  <PlusOutlined /> Someone else
                </span>
                <span className="sd-choice-sub">Enter a new contact for this SOW</span>
              </button>
            </div>
            {!values.clientId && <span className="sd-help">Choose a client to see their contacts.</span>}
          </Field>

          {values.pocChoice === 'new' && (
            <div className="sd-fieldset">
              <span className="sd-fieldset-title">New point of contact</span>
              <Field label="Name" required>
                <Input
                  size="large"
                  value={values.newPoc.name}
                  placeholder="Full name"
                  onChange={(e) => update({ newPoc: { ...values.newPoc, name: cleanName(e.target.value) } })}
                />
              </Field>
              <div className="sd-row">
                <Field label="Email" required>
                  <Input
                    size="large"
                    type="email"
                    value={values.newPoc.email}
                    placeholder="name@company.com"
                    onChange={(e) => update({ newPoc: { ...values.newPoc, email: e.target.value.trim() } })}
                  />
                </Field>
                <Field label="Mobile" required error={errors.pocNumber}>
                  <Input
                    size="large"
                    inputMode="tel"
                    maxLength={15}
                    value={values.newPoc.number}
                    status={errors.pocNumber ? 'error' : ''}
                    onBlur={() =>
                      values.newPoc.number &&
                      setErrors((prev) => ({ ...prev, pocNumber: mobileError(values.newPoc.number) || undefined }))
                    }
                    placeholder="+919876543210"
                    onChange={(e) =>
                      update({ newPoc: { ...values.newPoc, number: cleanMobile(e.target.value) } })
                    }
                  />
                </Field>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'commercials',
      title: 'Commercials',
      content: (
        <div className="sd-form">
          <Field label="Payment type" required>
            <div className="sd-entity" role="radiogroup" aria-label="Payment type">
              {[
                { value: 'T&M', title: 'Time & material', sub: 'Invoiced on a regular cycle' },
                { value: 'Milestones', title: 'Milestones', sub: 'Invoiced per deliverable' },
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  role="radio"
                  aria-checked={values.paymentType === type.value}
                  className={`sd-choice ${values.paymentType === type.value ? 'is-on' : ''}`}
                  onClick={() => update({ paymentType: type.value })}
                >
                  <span className="sd-choice-title">{type.title}</span>
                  <span className="sd-choice-sub">{type.sub}</span>
                </button>
              ))}
            </div>
          </Field>

          {values.paymentType === 'T&M' ? (
            <div className="sd-row">
              <Field label="Invoice every" required error={errors.invoiceTenure}>
                <Select
                  size="large"
                  value={values.invoiceTenure}
                  onChange={(invoiceTenure) => update({ invoiceTenure })}
                  options={INVOICE_TENURES}
                  status={errors.invoiceTenure ? 'error' : ''}
                />
              </Field>
              <Field
                label="Tentative invoice day"
                required
                error={errors.invoiceDate}
                help="Day of the month the invoice goes out"
              >
                <DatePicker
                  size="large"
                  format="DD"
                  value={values.invoiceDate}
                  allowClear={false}
                  inputReadOnly
                  onChange={(invoiceDate) => update({ invoiceDate })}
                />
              </Field>
            </div>
          ) : (
            <>
              <AmountInput
                label="SOW amount"
                currency={values.currency}
                amount={values.amount}
                onChange={update}
                error={errors.amount}
              />
              <Field label="Milestones" required error={errors.milestones}>
                <div className="sow-ms">
                  {values.milestones.length > 0 && (
                    <div className="sow-ms-head" aria-hidden="true">
                      <span>Name</span>
                      <span>%</span>
                      <span>Date</span>
                      <span />
                    </div>
                  )}
                  {values.milestones.map((m, index) => (
                    <div key={index} className="sow-ms-row">
                      <Input
                        aria-label={`Milestone ${index + 1} name`}
                        value={m.name}
                        placeholder="e.g. Design sign-off"
                        onChange={(e) => setMilestone(index, { name: cleanName(e.target.value) })}
                      />
                      <InputNumber
                        aria-label={`Milestone ${index + 1} percentage`}
                        min={1}
                        max={100}
                        value={m.percent}
                        placeholder="%"
                        onChange={(percent) => setMilestone(index, { percent })}
                        style={{ width: '100%' }}
                      />
                      <DatePicker
                        aria-label={`Milestone ${index + 1} date`}
                        format="DD/MM/YYYY"
                        value={m.date}
                        inputReadOnly
                        onChange={(date) => setMilestone(index, { date })}
                      />
                      <Button
                        type="text"
                        aria-label={`Remove milestone ${index + 1}`}
                        icon={<DeleteOutlined />}
                        onClick={() => update({ milestones: values.milestones.filter((_, i) => i !== index) })}
                      />
                    </div>
                  ))}
                  <div className="sow-ms-foot">
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() =>
                        update({
                          milestones: [
                            ...values.milestones,
                            { name: '', percent: total < 100 ? 100 - total : null, date: null },
                          ],
                        })
                      }
                    >
                      Add milestone
                    </Button>
                    <span className={`sow-ms-total ${total === 100 ? 'is-ok' : total > 100 ? 'is-over' : ''}`}>
                      Total {total}%
                    </span>
                  </div>
                </div>
              </Field>
            </>
          )}

          <div className="sd-row">
            <Field label="Payment term" required error={errors.paymentTerm}>
              <Select
                size="large"
                placeholder="Choose"
                value={values.paymentTerm}
                onChange={(paymentTerm) => update({ paymentTerm })}
                options={[
                  ...PAYMENT_TERMS.map((d) => ({ value: d, label: `Net ${d} days` })),
                  { value: 'other', label: 'Other' },
                ]}
                status={errors.paymentTerm ? 'error' : ''}
              />
            </Field>
            {values.paymentTerm === 'other' && (
              <Field label="Days" required>
                <InputNumber
                  size="large"
                  min={1}
                  max={365}
                  precision={0}
                  value={values.customTerm}
                  onChange={(customTerm) => update({ customTerm, paymentTerm: 'other' })}
                  style={{ width: '100%' }}
                />
              </Field>
            )}
          </div>

          <Field label="Leaves allowed" required error={errors.leavesAllowed}>
            <div className="sow-leaves">
              <Input
                size="large"
                inputMode="numeric"
                maxLength={3}
                value={values.leavesAllowed}
                status={errors.leavesAllowed ? 'error' : ''}
                placeholder="Days"
                onChange={(e) => update({ leavesAllowed: e.target.value.replace(/\D/g, '') })}
                suffix="days"
              />
              <Segmented
                value={values.leavesTerm}
                onChange={(leavesTerm) => update({ leavesTerm })}
                options={LEAVE_TERMS.map((t) => ({ value: t, label: `per ${t === 'Monthly' ? 'month' : t === 'Quarterly' ? 'quarter' : 'year'}` }))}
              />
            </div>
          </Field>
        </div>
      ),
    },
    {
      key: 'document',
      title: 'Document',
      content: (
        <div className="sd-form">
          <FileDrop
            file={file}
            existingName={existingFileName}
            onChange={(picked) => {
              setFile(picked);
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
            error={errors.file}
            label="Signed SOW"
          />
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <div className="sd-form">
          <Summary
            rows={[
              { label: 'Contracting entity', value: entityName(values.seId) },
              { label: 'Client', value: values.clientLabel },
              { label: 'Project', value: values.projectLabel },
              { label: 'SOW number', value: values.sowNumber },
              {
                label: 'Term',
                value: values.start && values.end ? `${formatDate(values.start)} → ${formatDate(values.end)}` : '',
              },
              {
                label: 'Client signatory',
                value: [values.signatoryName, values.signatoryEmail, values.signatoryMobile].filter(Boolean).join(' · '),
              },
              { label: 'Spanidea signatory', value: employeeLabel },
              {
                label: 'Point of contact',
                value: selectedPoc ? [selectedPoc.pocName, selectedPoc.pocEmail].filter(Boolean).join(' · ') : '',
              },
              {
                label: 'Payment',
                value:
                  values.paymentType === 'T&M'
                    ? `Time & material · invoiced ${(
                        INVOICE_TENURES.find((t) => t.value === values.invoiceTenure)?.label || ''
                      ).toLowerCase()} on day ${values.invoiceDate?.format('D')}`
                    : `${values.milestones.length} milestone${values.milestones.length === 1 ? '' : 's'} · ${total}%`,
              },
              ...(values.paymentType === 'Milestones'
                ? [{ label: 'SOW amount', value: formatAmount(values.currency, values.amount) }]
                : []),
              { label: 'Payment term', value: termDays ? `Net ${termDays} days` : '' },
              {
                label: 'Leaves allowed',
                value: values.leavesAllowed ? `${values.leavesAllowed} days, ${values.leavesTerm.toLowerCase()}` : '',
              },
              { label: 'Document', value: file?.name || existingFileName },
            ]}
          />
          {canSendForApproval && (
            <span className="sd-help">
              Sending for approval moves the SOW to Pending approval. You can also keep it as a draft.
            </span>
          )}
        </div>
      ),
    },
  ];

  const draftLabel = isEdit ? 'Save changes' : 'Save as draft';

  return (
    <StepDrawer
      open={open}
      width={760}
      title={isEdit ? 'Edit SOW' : 'New SOW'}
      subtitle={[values.clientLabel, values.projectLabel].filter(Boolean).join(' · ') || 'Statement of work'}
      steps={steps}
      current={step}
      onStepClick={(index) => (index < step ? setStep(index) : goNext())}
      onBack={() => setStep(step - 1)}
      onNext={goNext}
      onClose={() => onClose(false)}
      footerStart={
        canSendForApproval && (
          <Button onClick={() => save(STATUS.draft)} loading={saving === 'draft'} disabled={!!saving}>
            {draftLabel}
          </Button>
        )
      }
      submitButton={
        canSendForApproval ? (
          <Button
            type="primary"
            onClick={() => save(STATUS.pending)}
            loading={saving === 'approval'}
            disabled={!!saving}
          >
            Send for approval
          </Button>
        ) : (
          <Button type="primary" onClick={() => save(STATUS.draft)} loading={saving === 'draft'} disabled={!!saving}>
            {draftLabel}
          </Button>
        )
      }
    />
  );
};

export default SowDrawer;
